import { FieldLabel } from "../workspaces/shared/FieldLabel";
import { useState } from "react";
import { userMessage } from "../workspaces/shared/userMessage";
import { Callout, Dialog } from "../workspaces/shared/ui";
import { Bug, CheckCircle2, HelpCircle, Lightbulb, MessageCircle, type LucideIcon } from "lucide-react";

const KIND_ICONS: Record<string, LucideIcon> = { bug: Bug, confusing: HelpCircle, feature: Lightbulb, general: MessageCircle };

/**
 * SEND FEEDBACK / REPORT A BUG (Phase 13B §58I, extended in 13C §59D).
 *
 * A tester writes what happened; the application adds only what it needs to
 * make sense of it — who, where, when, which version. No screenshot, no
 * project contents, no financial figures are attached, and the dialog says
 * so before anything is sent.
 *
 * 13C added the two fields that make a bug report usable: what they were
 * trying to do, and the reference Berrier Reno showed them when it broke.
 */

const KINDS: { id: string; label: string }[] = [
  { id: "bug", label: "Problem / bug" },
  { id: "confusing", label: "Confusing / need help" },
  { id: "feature", label: "Feature idea" },
  { id: "general", label: "General feedback" },
];

export function FeedbackDialog({
  area,
  onClose,
  projectId,
}: {
  area: string;
  onClose: () => void;
  /** Spec §86: the project on screen, so the report says what it is about. */
  projectId?: string | null;
}) {
  const [kind, setKind] = useState("bug");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [context, setContext] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          title,
          message,
          route: `${area}${typeof window !== "undefined" ? ` ${window.location.pathname}` : ""}`.slice(0, 80),
          projectId: projectId ?? undefined,
          context,
          diagnosticReference: reference.trim().toUpperCase(),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(payload.error ?? "That could not be sent.");
      setSent(true);
      setError(null);
    } catch (err) {
      setError(userMessage(err, "That could not be sent."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={sent ? "Thank you" : "Send feedback"} onClose={onClose} labelledBy="feedback-title">
      {sent ? (
        <div className="auth-success">
          <span className="auth-success-icon" aria-hidden="true">
            <CheckCircle2 size={26} />
          </span>
          <p>It has gone to Berrier Holdings. If it was a problem, it will be read alongside the error record from the same moment.</p>
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      ) : (
        <form className="auth-form feedback-form" onSubmit={(event) => void submit(event)}>
          <div className="feedback-kinds" role="radiogroup" aria-label="What kind of feedback is this?">
            {KINDS.map((entry) => {
              const Icon = KIND_ICONS[entry.id];
              return (
                <FieldLabel key={entry.id} className={`feedback-kind ${kind === entry.id ? "feedback-kind-selected" : ""}`}>
                  <input type="radio" name="feedback-kind" checked={kind === entry.id} onChange={() => setKind(entry.id)} />
                  <Icon size={18} aria-hidden="true" />
                  <span>{entry.label}</span>
                </FieldLabel>
              );
            })}
          </div>
          <FieldLabel>
            <span>
              In a few words <span className="muted form-optional">optional</span>
            </span>
            <input type="text" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} placeholder="e.g. Can't find where to add a room" />
          </FieldLabel>
          <FieldLabel>
            Tell us more
            <textarea rows={5} value={message} onChange={(event) => setMessage(event.target.value)} placeholder="What did you see, and what did you expect instead?" required />
          </FieldLabel>
          <details className="feedback-more">
            <summary>Add what you were doing, or an error reference</summary>
            <FieldLabel>
              What were you trying to do?
              <textarea rows={2} value={context} onChange={(event) => setContext(event.target.value)} placeholder="Adding a change order to a project I had just baselined…" />
            </FieldLabel>
            <FieldLabel>
              Reference, if Berrier Reno showed you one
              <input type="text" value={reference} onChange={(event) => setReference(event.target.value)} placeholder="e.g. 7QK4TP" maxLength={20} spellCheck={false} />
            </FieldLabel>
          </details>
          <p className="auth-hint">
            Sent with this: your account, the screen you are on ({area || "the application"}){projectId ? ", which project" : ""}, the version and the time. No
            screenshot, no project data, no amounts — if a figure matters, type it yourself.
          </p>
          {error && <Callout tone="bad">{error}</Callout>}
          <div className="dialog-actions">
            <button className="btn btn-ghost" type="button" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" type="submit" disabled={busy || message.trim().length === 0}>
              {busy ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
